// components/common/OptimizedImage.tsx
// Uses next/image for automatic WebP/AVIF conversion, LCP optimization, and proper lazy loading
import React, { useState } from "react";
import Image from "next/image";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  aspectRatio?: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
  style?: React.CSSProperties;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  isObjectFit?: boolean;
  imageClassName?: string;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width = 800,
  height = 600,
  aspectRatio = "4/3",
  className = "",
  priority = false,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  style,
  objectFit = "cover",
  isObjectFit = true,
  imageClassName = "",
}) => {
  const [loaded, setLoaded] = useState(false);

  // Fallback for empty/missing src
  const imgSrc = src && src.trim() !== "" ? src : "/images/placeholder.jpg";

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ aspectRatio, backgroundColor: "#f1f5f9", ...style }}
    >
      <Image
        src={imgSrc}
        alt={alt || "Image"}
        width={width}
        height={height}
        priority={priority}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        sizes={sizes}
        onLoad={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full ${isObjectFit ? `object-${objectFit}` : ""} transition-all duration-300 ${
          loaded ? "opacity-100" : "opacity-0"
        } ${imageClassName}`}
        unoptimized={
          // Only unoptimize if src is an external URL not in next.config domains
          // External images from configured domains will be optimized
          false
        }
      />
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gray-200" />
      )}
    </div>
  );
};
